import { Vertical, Horizontal, GraviGrid, Texto, BBDTag } from '@gravitate-js/excalibrr'
import { Drawer } from 'antd'
import type { ColDef, Context, GridApi } from 'ag-grid-community'

import { useRef, useState, useEffect, type MutableRefObject } from 'react'
import type { RateHistoryDrawerProps } from '../types'
import { PointToPointScheduleButton } from '@components/Freight/Ratebooks/Drill-in/components/ScheduleRateButton'
import { RateStatusTag } from '@components/Freight/Ratebooks/Drill-in/components/RateStatusTag'
import { formatDate, formatRate, formatDistance, getMostRecentActiveRateId } from '../utils'
import { DeleteRateButton } from './DeleteRateButton'
import { EditRateButton } from './EditRateButton'
import type { PointToPoint, Rate } from '@api/useFreight/types'
import dayjs from 'dayjs'
import { useQueryClient } from '@tanstack/react-query'
import type { AgGridReact } from 'ag-grid-react'

interface RateHistoryContext extends Context {
  pointToPoint: PointToPoint
  onRateChange: () => void
  setPointToPoint: (pointToPoint: PointToPoint) => void
  mostRecentActiveRateId: string
}

// Define column definitions outside component
const columnDefs: ColDef[] = [
  {
    headerName: 'Status',
    minWidth: 120,
    cellRenderer: ({ data, context }: { data: Rate; context: any }) => {
      if (!data) return null
      return <RateStatusTag rate={data} isMostRecentActive={data.id === context.mostRecentActiveRateId} />
    },
  },
  {
    headerName: 'From',
    field: 'effective_from',
    minWidth: 100,
    cellRenderer: ({ value }: { value: string }) => <Texto>{formatDate(value)}</Texto>,
  },
  {
    headerName: 'To',
    field: 'effective_to',
    minWidth: 100,
    cellRenderer: ({ value }: { value: string | null }) =>
      value ? <Texto>{formatDate(value)}</Texto> : <Texto appearance='hint'>Indefinite</Texto>,
  },
  {
    headerName: 'Rate',
    field: 'rate',
    minWidth: 100,
    cellStyle: {
      textAlign: 'right',
      fontWeight: 'bold',
    },
    type: 'numericColumn',
    valueFormatter: ({ value }: { value: number }) => formatRate(value),
  },
  {
    headerName: 'Distance',
    field: 'distance',
    minWidth: 100,
    valueFormatter: ({ value }: { value: number }) => formatDistance(value),
  },
  {
    headerName: '',
    minWidth: 100,
    maxWidth: 100,
    pinned: 'right',
    cellRenderer: ({ data, context }: { data: Rate; context: RateHistoryContext }) => {
      if (!data) return null

      const now = dayjs()
      const effectiveFrom = dayjs(data.effective_from)
      const isScheduled = effectiveFrom.isAfter(now)

      return (
        <Horizontal gap={8} verticalCenter style={{ overflow: 'visible', width: '100%' }}>
          <EditRateButton
            rate={data}
            pointToPoint={context.pointToPoint}
            onSuccess={context.onRateChange}
            setPointToPoint={context.setPointToPoint}
          />
          {isScheduled && (
            <DeleteRateButton
              rate={data}
              pointToPoint={context.pointToPoint}
              onSuccess={context.onRateChange}
              setPointToPoint={context.setPointToPoint}
            />
          )}
        </Horizontal>
      )
    },
  },
]

/**
 * Component for displaying and managing rate history
 */
export function RateHistoryDrawer({
  pointToPoint: initialPointToPoint,
  onRateChange,
  open,
  onClose,
}: RateHistoryDrawerProps) {
  const gridRef = useRef<AgGridReact>(null)
  const [pointToPoint, setPointToPoint] = useState(initialPointToPoint)
  const queryClient = useQueryClient()

  // Update local state when props change
  useEffect(() => {
    setPointToPoint(initialPointToPoint)
  }, [initialPointToPoint])

  // Refresh data when drawer opens
  useEffect(() => {
    if (open) {
      queryClient.invalidateQueries({ queryKey: ['ratebook/p2p'] })
    }
  }, [open, queryClient])

  // Calculate most recent active rate ID
  const mostRecentActiveRateId = getMostRecentActiveRateId(pointToPoint.rates)

  return (
    <Drawer
      styles={{
        body: {
          padding: 0,
        },
      }}
      title={
        <Vertical verticalCenter gap={8}>
          <Texto category='h5' weight='600'>
            Rate History
          </Texto>
          <Horizontal>
            <Vertical gap={8}>
              <Horizontal gap={8}>
                <Texto>Origin:</Texto>
                <Texto appearance='hint'>{pointToPoint.origin}</Texto>
              </Horizontal>
              <Horizontal>
                <Texto>Destination:</Texto>
                <Texto appearance='hint'>{pointToPoint.destination}</Texto>
              </Horizontal>
            </Vertical>
            <Vertical gap={8} alignItems='space-between'>
              <Horizontal justifyContent='space-between' flex={1}>
                <Texto>Product Group:</Texto>
                <Texto appearance='hint'>{pointToPoint.product_group}</Texto>
              </Horizontal>
              <PointToPointScheduleButton
                pointToPoint={pointToPoint}
                onSuccess={onRateChange}
                setPointToPoint={setPointToPoint}
              />
            </Vertical>
          </Horizontal>
        </Vertical>
      }
      open={open}
      onClose={onClose}
      width={600}
    >
      <Vertical gap={16}>
        <GraviGrid
          key={`rate-history-${pointToPoint._id || pointToPoint.id}-${pointToPoint.rates?.length}`}
          storageKey='point_to_point_rate_list'
          rowData={pointToPoint.rates || []}
          agPropOverrides={{
            getRowId: (params) => params.data.id,
            rowGroupPanelShow: 'never',
            rowHeight: 40,
            context: {
              pointToPoint,
              onRateChange,
              setPointToPoint,
              mostRecentActiveRateId,
            },
            columnDefs: columnDefs,
          }}
          controlBarProps={{
            title: `Rate History (${pointToPoint.rates?.length || 0})`,
            customSearchBar: <div />,
          }}
          externalRef={gridRef as unknown as MutableRefObject<GridApi>}
        />
      </Vertical>
    </Drawer>
  )
}
